import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Eye, EyeOff } from "lucide-react";
import { api, errMsg, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountrySelect, CountryNotice } from "@/components/CountrySelect";

const Shell = ({ title, subtitle, children }) => (
  <div className="grid min-h-[80vh] md:grid-cols-2">
    <div className="relative hidden overflow-hidden bg-green-900 md:block">
      <img src={imgUrl("/api/uploads/demo/d16.jpg")} alt="" className="h-full w-full object-cover opacity-60" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <div className="absolute bottom-10 left-10 max-w-sm text-white">
        <p className="font-display text-4xl font-black leading-tight">Achetez et vendez partout en Afrique.</p>
        <div className="kente mt-4 h-2 w-40 rounded" />
      </div>
    </div>
    <div className="flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-6">
        <Logo />
        <div><h1 className="font-display text-3xl font-black tracking-tight">{title}</h1>{subtitle && <p className="mt-1 text-gray-500">{subtitle}</p>}</div>
        {children}
      </div>
    </div>
  </div>
);

const PasswordInput = ({ id, ...p }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} data-testid={`${id}-input`} {...p} type={show ? "text" : "password"} className="pr-11" />
      <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${id}-toggle`} aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"} aria-pressed={show}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-800 transition-colors hover:text-orange-600">
        {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );
};

const F = ({ id, label, type, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label>{type === "password" ? <PasswordInput id={id} {...p} /> : <Input id={id} type={type} data-testid={`${id}-input`} {...p} />}</div>
);

const DevLink = ({ link, label }) => link ? (
  <div data-testid="dev-link-box" className="rounded-xl bg-indigo-50 p-4 text-sm text-indigo-900 ring-1 ring-indigo-200">
    <p className="font-bold">Mode test : l'envoi d'e-mails est désactivé.</p>
    <Link to={link} data-testid="dev-link" className="mt-1 inline-block font-semibold underline">{label}</Link>
  </div>
) : null;

const GoogleButton = () => (
  <Button type="button" variant="outline" className="w-full rounded-full" data-testid="google-login-btn"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/compte";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}>
    <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    Continuer avec Google
  </Button>
);

export function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data.user);
      toast.success("Bienvenue !");
      nav(params.get("next") || (data.user.role === "admin" ? "/admin" : data.user.shop ? "/vendeur" : "/"));
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <Shell title="Connexion" subtitle="Heureux de vous revoir sur Casadeal.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <F id="login-email" label="E-mail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <F id="login-password" label="Mot de passe" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <Link to="/mot-de-passe-oublie" data-testid="forgot-link" className="block text-right text-sm font-semibold text-orange-600">Mot de passe oublié ?</Link>
        <Button className="w-full rounded-full" size="lg" disabled={busy} data-testid="login-submit">{busy ? "Connexion…" : "Se connecter"}</Button>
      </form>
      <GoogleButton />
      <p className="text-center text-sm">Pas encore de compte ? <Link to="/inscription" className="font-semibold text-orange-600" data-testid="to-register">Créer un compte</Link></p>
    </Shell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm_password: "", country_code: "SN" });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [done, setDone] = useState(false);
  const { setUser } = useAuth();
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) return toast.error("Les mots de passe ne correspondent pas");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data.user);
      setLink(data.dev_verification_link);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  if (done)
    return (
      <Shell title="Compte créé" subtitle="Vous pouvez dès maintenant acheter sur Casadeal.">
        {link ? <DevLink link={link} label="Valider mon adresse e-mail" /> : (
          <p data-testid="register-verify-info" className="rounded-xl bg-orange-50 p-4 text-sm text-gray-800 ring-1 ring-orange-200">La validation de votre adresse e-mail est nécessaire uniquement pour devenir vendeur. Contactez Casadeal : l'administrateur la valide après vérification.</p>
        )}
        <Button asChild className="w-full rounded-full" data-testid="register-continue"><Link to="/">Continuer mes achats</Link></Button>
      </Shell>
    );
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Shell title="Créer un compte" subtitle="Un seul compte pour acheter, et vendre plus tard si vous le souhaitez.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <F id="register-name" label="Nom complet" required value={form.name} onChange={set("name")} />
        <F id="register-email" label="E-mail" type="email" required value={form.email} onChange={set("email")} />
        <F id="register-phone" label="Téléphone (facultatif)" value={form.phone} onChange={set("phone")} />
        <div className="space-y-1.5"><Label>Pays</Label><CountrySelect testid="register-country" value={form.country_code} onChange={(v) => setForm({ ...form, country_code: v })} /><CountryNotice code={form.country_code} /></div>
        <F id="register-password" label="Mot de passe (8 caractères min.)" type="password" minLength={8} required value={form.password} onChange={set("password")} />
        <F id="register-confirm" label="Confirmer le mot de passe" type="password" required value={form.confirm_password} onChange={set("confirm_password")} />
        <Button className="w-full rounded-full" size="lg" disabled={busy} data-testid="register-submit">{busy ? "Création…" : "Créer mon compte"}</Button>
      </form>
      <GoogleButton />
      <p className="text-center text-sm">Déjà inscrit ? <Link to="/connexion" className="font-semibold text-orange-600">Se connecter</Link></p>
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const [msg, setMsg] = useState("");
  const { refresh } = useAuth();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(() => { setState("ok"); refresh(); })
      .catch((e) => { setState("error"); setMsg(errMsg(e)); });
  }, [params, refresh]);
  return (
    <Shell title="Validation de l'e-mail">
      {state === "loading" && <Loader2 className="h-6 w-6 animate-spin text-orange-500" />}
      {state === "ok" && <p data-testid="verify-success" className="flex items-center gap-2 font-semibold text-green-800"><CheckCircle2 className="h-5 w-5" />Adresse e-mail validée.</p>}
      {state === "error" && <p data-testid="verify-error" className="text-red-600">{msg}</p>}
      <Button asChild className="rounded-full"><Link to="/compte">Aller à mon compte</Link></Button>
    </Shell>
  );
}

const ContactButtons = () => {
  const [phone, setPhone] = useState(null);
  useEffect(() => {
    api.get("/subscriptions/contact").then((r) => setPhone(r.data.phone)).catch(() => {});
  }, []);
  if (!phone) return null;
  const wa = `https://wa.me/${phone.replace(/\D/g, "")}?text=${encodeURIComponent("Bonjour Casadeal, je souhaite réinitialiser le mot de passe de mon compte.")}`;
  return (
    <div className="flex flex-wrap gap-2">
      <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid="forgot-contact-phone" className="rounded-full bg-orange-500 px-4 py-2 text-sm font-bold text-white">{phone}</a>
      <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="forgot-contact-whatsapp" className="rounded-full bg-[#25D366] px-4 py-2 text-sm font-bold text-white">WhatsApp</a>
    </div>
  );
};

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { setRes((await api.post("/auth/forgot-password", { email })).data); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Mot de passe oublié" subtitle="Indiquez votre e-mail pour lancer la procédure de réinitialisation.">
      {res ? (
        <div className="space-y-3"><p data-testid="forgot-message" className="text-sm text-gray-700">{res.message}</p><DevLink link={res.dev_reset_link} label="Réinitialiser mon mot de passe" /><ContactButtons /></div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <F id="forgot-email" label="E-mail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button className="w-full rounded-full" data-testid="forgot-submit">Continuer</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ password: "", confirm_password: "" });
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) return toast.error("Les mots de passe ne correspondent pas");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", ...form });
      toast.success("Mot de passe modifié. Connectez-vous.");
      nav("/connexion");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Nouveau mot de passe">
      <form onSubmit={submit} className="space-y-4">
        <F id="reset-password" label="Nouveau mot de passe" type="password" minLength={8} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <F id="reset-confirm" label="Confirmer" type="password" required value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} />
        <Button className="w-full rounded-full" data-testid="reset-submit">Enregistrer</Button>
      </form>
    </Shell>
  );
}

export function AuthCallback() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data.user); nav(loc.pathname, { replace: true }); })
      .catch((e) => { toast.error(errMsg(e)); setUser(false); nav("/connexion", { replace: true }); });
  }, [loc, nav, setUser]);
  return null;
}
